import { useEffect, useState } from "react";
import { Loader2, Check, AtSign } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { updateUserProfile } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Fix a person's name and sign-in email. Used from the account menu (your
 * own) and from Settings → Users & Access (the owner fixing somebody's).
 *
 * The email is the login and we can't send a verification message, so a
 * typo would lock that person out of both the app and /forgot-password.
 * Hence the confirm field, which only appears once the address actually
 * differs — it's the one mistake nobody can undo for themselves.
 */
export default function ProfileDialog({ person, open, onOpenChange, onSaved }) {
  const { user: signedIn } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && person) {
      setName(person.name || "");
      setEmail(person.email || "");
      setConfirm("");
    }
  }, [open, person]);

  if (!person) return null;

  const emailChanged = email.trim().toLowerCase() !== (person.email || "").toLowerCase();
  const nameChanged = name.trim() !== (person.name || "");
  const isSelf = signedIn?.id === person.id;

  const save = async () => {
    if (!name.trim()) { toast.error("Enter a name"); return; }
    if (emailChanged && confirm.trim().toLowerCase() !== email.trim().toLowerCase()) {
      toast.error("The two email addresses don't match");
      return;
    }
    if (!nameChanged && !emailChanged) { onOpenChange(false); return; }
    setSaving(true);
    try {
      const res = await updateUserProfile(person.id, {
        name: name.trim(),
        ...(emailChanged ? { email: email.trim() } : {}),
      });
      toast.success(
        res.changed.includes("email")
          ? `Saved — sign in with ${res.email} from now on`
          : "Saved"
      );
      onSaved?.(res);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save that");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black">
            {isSelf ? "Your details" : `${person.name || "This person"}'s details`}
          </DialogTitle>
          <DialogDescription>
            {isSelf
              ? "The name shown on jobs and paperwork, and the email you sign in with."
              : "The name shown on jobs and paperwork, and the email they sign in with."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Jeremy Smith"
              className="mt-1"
              data-testid="profile-name-input"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Sign-in email
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1"
              data-testid="profile-email-input"
            />
          </div>
          {emailChanged && (
            <div data-testid="profile-email-confirm-wrap">
              <label className="text-xs font-bold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                Type the new email again
              </label>
              <Input
                type="email"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1"
                placeholder="Confirm the new address"
                data-testid="profile-email-confirm-input"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 flex gap-1.5">
                <AtSign className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>
                  This becomes the address {isSelf ? "you sign in" : "they sign in"} with.
                  We can't check it works, so a typo would lock {isSelf ? "you" : "them"} out —
                  only the owner could put it right.
                </span>
              </p>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="profile-cancel">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="profile-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
